(() => {
  const config = window.VipClubAutoAdd;
  if (!config?.variantId || !config?.sellingPlanId || !window.fetch) return;

  const originalFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = new URL(`${root}cart.js`, window.location.origin).href;
  const addUrl = new URL(window.routes?.cart_add_url || `${root}cart/add`, window.location.origin).href;
  const addJsUrl = new URL(`${root}cart/add.js`, window.location.origin).href;
  const changeJsUrl = new URL(`${root}cart/change.js`, window.location.origin).href;
  const cartSections = ['cart-drawer', 'cart-icon-bubble'];
  const vipItem = () => ({ id: Number(config.variantId), quantity: 1, selling_plan: Number(config.sellingPlanId) });

  const isCartAdd = (input) => {
    const url = typeof input === 'string' ? input : input?.url;
    return Boolean(url) && new URL(url, window.location.origin).href === addUrl;
  };

  const isVip = (id) => String(id) === String(config.variantId);
  const hasVipVariant = (formData) => Array.from(formData.entries()).some(([key, value]) =>
    (key === 'id' || /\[id\]$/.test(key)) && isVip(value)
  );
  const cartHasVip = (cart) => cart?.items?.some((item) => isVip(item.variant_id));

  let redirectingToCheckout = false;
  const checkoutUrl = new URL(`${root}checkout`, window.location.origin).href;
  // Back from the checkout restores this page exactly as it was left: mid add-to-cart, with the
  // theme's button still marked busy (it ignores taps while busy). Put the buttons back.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    redirectingToCheckout = false;
    for (const button of document.querySelectorAll('button[name="add"]')) {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  });

  const redirectToCheckout = (cart, added) => {
    redirectingToCheckout = true;
    // The custom checkout takes over when it is installed; otherwise Shopify's own checkout.
    if (window.RiftCheckout?.goToCheckout) {
      window.RiftCheckout.goToCheckout(cart, added);
      return;
    }
    window.location.assign(checkoutUrl);
  };

  /** The one product line a standard product form submits, as a /cart/add.js item. */
  const itemFromForm = (formData) => {
    const id = formData.get('id');
    if (!id) return null;
    const item = { id: Number(id), quantity: Math.max(1, Number(formData.get('quantity')) || 1) };
    if (formData.get('selling_plan')) item.selling_plan = Number(formData.get('selling_plan'));
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  };

  // The old path, kept for unusual forms (several products at once): the theme's own request,
  // then VIP, then the checkout.
  async function slowPath(input, init) {
    let cart;
    try {
      const response = await originalFetch(cartUrl, { credentials: 'same-origin' });
      if (!response.ok) return originalFetch(input, init);
      cart = await response.json();
    } catch (_) {
      return originalFetch(input, init);
    }
    const primaryResponse = await originalFetch(input, init);
    if (!primaryResponse.ok) return primaryResponse;
    if (!cartHasVip(cart)) {
      try {
        const body = new FormData();
        body.append('id', String(config.variantId));
        body.append('quantity', '1');
        body.append('selling_plan', String(config.sellingPlanId));
        body.append('sections', cartSections.join(','));
        body.append('sections_url', `${window.location.pathname}${window.location.search}`);
        const vipResponse = await originalFetch(addUrl, { ...init, method: 'POST', body });
        if (!vipResponse.ok) return primaryResponse;
      } catch (_) {
        return primaryResponse;
      }
    }
    redirectToCheckout();
    return primaryResponse;
  }

  window.fetch = async (input, init = undefined) => {
    if (!isCartAdd(input) || !init?.body || !(init.body instanceof FormData)) {
      return originalFetch(input, init);
    }
    const submitted = init.body;
    if (!submitted.has('id') && !Array.from(submitted.keys()).some((key) => /^items\[\d+\]\[id\]$/.test(key))) {
      return originalFetch(input, init);
    }
    if (hasVipVariant(submitted) || redirectingToCheckout) return originalFetch(input, init);

    const item = itemFromForm(submitted);
    if (!item) return slowPath(input, init);

    // Fast path: one request adds the product and VIP together, and skips the cart-drawer
    // re-render the theme asks for (about 1 second) because the shopper is leaving this page.
    // VIP is always sent; if it is already in the cart, the quantity is put back to 1 below.
    const items = [item, vipItem()];

    let addResponse;
    try {
      addResponse = await originalFetch(addJsUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
    } catch (_) {
      return originalFetch(input, init);
    }
    if (!addResponse.ok) {
      // Sold out, invalid quantity, …: hand the theme the real error so it shows its own message.
      return addResponse;
    }
    const added = await addResponse.clone().json().catch(() => null);

    // Read the cart as Shopify now has it, so the checkout gets exactly what is in the cart.
    let cart = await originalFetch(cartUrl, { credentials: 'same-origin' }).then((r) => (r.ok ? r.json() : null)).catch(() => null);

    // VIP must be in the cart exactly once.
    if (cart) {
      try {
        const vipLines = cart.items.filter((line) => isVip(line.variant_id));
        if (vipLines.length === 0) {
          const response = await originalFetch(addJsUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [vipItem()] }) });
          cart = response.ok ? await originalFetch(cartUrl, { credentials: 'same-origin' }).then((r) => r.json()) : cart;
        } else if (vipLines.some((line) => line.quantity > 1)) {
          const response = await originalFetch(changeJsUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: vipLines[0].key, quantity: 1 }) });
          if (response.ok) cart = await response.json();
        }
      } catch (_) {
        cart = null; // the handoff reads the cart itself
      }
    }

    redirectToCheckout(cart || undefined, { variantId: item.id, quantity: item.quantity });

    // The shopper is on their way to the checkout. Keep the theme's button in its loading state
    // rather than letting it redraw a cart drawer nobody will see; if, for any reason, the page is
    // still here after 15 seconds, answer it normally.
    return new Promise((resolve) => {
      setTimeout(() => resolve(new Response(JSON.stringify(added?.items?.[0] || {}), { status: 200, headers: { 'Content-Type': 'application/json' } })), 15000);
    });
  };
})();
